import React from "react";

import { REQUEST_GROUPS } from "./portal-data";
import type { PortalRoute } from "./portal-routing";

/* Placeholders for the portal's three loading surfaces.
 *
 * The portal used to answer every wait with one centred spinner over an empty
 * page — the same picture for "reading your profile", "listing requests" and
 * "checking twelve pull requests against GitHub". A placeholder that carries
 * the shape of what is coming says more, and it says it in the position the
 * content will actually occupy, so nothing jumps when the content lands.
 */

/** The portal's spinner, for the waits that have no shape to draw. */
export function Spinner({ label = "Loading" }: { label?: string }) {
  return <span className="portal-spinner" role="status"><i aria-hidden="true" />{label}</span>;
}

const CARD_WIDTHS = ["82%", "64%", "91%", "58%", "74%"];

function bar(index: number) {
  return CARD_WIDTHS[index % CARD_WIDTHS.length];
}

/** Screen-reader announcement. The placeholder itself is decorative. */
export function PortalLoadingLabel({ children }: { children: string }) {
  return <span className="portal-skeleton-label" role="status">{children}</span>;
}

export function PortalSkeletonBar({
  width = "100%",
  height = 10,
}: {
  width?: number | string;
  height?: number | string;
}) {
  return <span className="portal-skeleton" style={{ width, height }} aria-hidden="true" />;
}

/** The request board, drawn from its own column definitions.
 *
 *  The four columns and their headings are a constant — they do not depend on
 *  a single request — so there is no reason to wait for the network to draw
 *  them. What is unknown is only how many cards land in each.
 */
export function BoardSkeleton() {
  return <div className="portal-board" aria-busy="true">
    <PortalLoadingLabel>Loading requests</PortalLoadingLabel>
    {REQUEST_GROUPS.map((group, column) => <section
      className={`portal-board-column ${group.key}`}
      key={group.key}
    >
      <header>
        <div><h2>{group.label}</h2><p>{group.description}</p></div>
        {/* No count. A "0" standing where a number is about to arrive is a
            claim that the column is empty, and for the second it is on screen
            it is usually a false one. */}
        <span className="portal-skeleton portal-skeleton-count" aria-hidden="true" />
      </header>
      <div className="portal-card-stack" aria-hidden="true">
        {Array.from({ length: column === 3 ? 1 : 2 }, (_, card) => <div
          className="portal-request-card portal-card-skeleton"
          key={card}
        >
          <PortalSkeletonBar width={72} height={16} />
          <PortalSkeletonBar width={bar(column + card)} height={13} />
          <PortalSkeletonBar width={bar(column + card + 2)} height={9} />
          <footer><PortalSkeletonBar width={104} height={9} /></footer>
        </div>)}
      </div>
    </section>)}
  </div>;
}

/** Table body placeholder, dropped straight into an existing `<tbody>`. */
export function PortalRowsSkeleton({
  label,
  columns,
  rows = 4,
}: {
  label: string;
  columns: number;
  rows?: number;
}) {
  return <>
    <tr className="portal-skeleton-announcement">
      <td colSpan={columns}><PortalLoadingLabel>{label}</PortalLoadingLabel></td>
    </tr>
    {Array.from({ length: rows }, (_, row) => <tr className="portal-skeleton-row" key={row} aria-hidden="true">
      {Array.from({ length: columns }, (_, column) => <td key={column}>
        <PortalSkeletonBar width={bar(row + column)} height={9} />
        {column === 0 && <PortalSkeletonBar width="52%" height={8} />}
      </td>)}
    </tr>)}
  </>;
}

/** One request's detail page: heading, conversation, and evidence rail. */
export function RequestDetailSkeleton() {
  return <div className="portal-detail-skeleton" aria-busy="true">
    <PortalLoadingLabel>Loading request</PortalLoadingLabel>
    <section className="portal-detail-heading" aria-hidden="true">
      <div>
        <div className="portal-heading-meta">
          <PortalSkeletonBar width={78} height={18} />
          <PortalSkeletonBar width={116} height={11} />
        </div>
        <PortalSkeletonBar width="58%" height={22} />
      </div>
    </section>
    <div className="portal-detail-skeleton-body" aria-hidden="true">
      <div className="portal-panel">
        {[0, 1, 2].map((row) => <div className="portal-detail-skeleton-message" key={row}>
          <PortalSkeletonBar width={92} height={9} />
          <PortalSkeletonBar width={bar(row)} height={11} />
          <PortalSkeletonBar width={bar(row + 1)} height={11} />
        </div>)}
      </div>
      <div className="portal-panel">
        {[0, 1].map((row) => <div className="portal-detail-skeleton-message" key={row}>
          <PortalSkeletonBar width={72} height={9} />
          <PortalSkeletonBar width={bar(row + 3)} height={11} />
        </div>)}
      </div>
    </div>
  </div>;
}

/** What to draw while the portal is still working out who you are.
 *
 *  Keyed to the route, because the route is already known — it came out of the
 *  URL, before any request was made. Showing the destination's own shape means
 *  the bootstrap wait and the view's own first load look like one continuous
 *  load rather than a spinner followed by a different spinner.
 *
 *  The admin views are the exception, and deliberately: their placeholder would
 *  be a promise about permissions we have not checked yet, and a non-admin
 *  landing on a bookmarked admin URL would watch a merge desk draw itself and
 *  then be taken away. They wait on a plain spinner.
 */
export function PortalRouteSkeleton({ route }: { route: PortalRoute }) {
  if (route.view === "requests") return <BoardSkeleton />;
  if (route.view === "request") return <RequestDetailSkeleton />;
  return <div className="portal-page-loading"><Spinner label="Opening Netra" /></div>;
}
